import type { TFn } from '@/i18n/LocaleProvider';

/**
 * Which published desktop build, if any, belongs to the browser asking.
 *
 * Kept apart from the component so the matching is testable without a DOM, because the matching is
 * the part that can be wrong in a way nobody notices: a button that downloads the *other* Mac build
 * looks identical to one that works, right up until someone runs it.
 */

/** One build the deployment can hand over, as `GET /desktop/download` describes it. */
export interface IDesktopDownload {
  target: string;
  arch: string;
  version: string;
  fileName: string;
  sizeBytes: number;
  publishedAt: string;
  notes: string | null;
}

/** What the browser could work out about itself. Either half may be unknown. */
export interface IBrowserPlatform {
  target: 'darwin' | 'windows' | 'linux' | null;
  arch: 'aarch64' | 'x86_64' | null;
}

/**
 * Read the platform off a user-agent string, and the architecture only if something reliable said
 * so.
 *
 * ⚠️ **The user-agent string does not know the architecture and must not be asked.** Every Apple
 * Silicon Mac reports `Intel Mac OS X 10_15_7`, unchanged since 2020, so inferring `x86_64` from it
 * would be confidently wrong on the majority of Macs in use. `navigator.userAgentData` answers
 * honestly on Chromium and is absent on Safari and Firefox — so the architecture is `null` far more
 * often than not, and everything downstream is built to cope with that rather than to guess.
 */
export function detectPlatform(
  userAgent: string,
  architectureHint: string | null = null,
): IBrowserPlatform {
  const agent = userAgent.toLowerCase();
  const target =
    agent.includes('mac os') || agent.includes('macintosh')
      ? 'darwin'
      : agent.includes('windows')
        ? 'windows'
        : agent.includes('linux') || agent.includes('x11')
          ? 'linux'
          : null;

  const hint = (architectureHint ?? '').toLowerCase();
  const arch = hint === 'arm' ? 'aarch64' : hint === 'x86' ? 'x86_64' : null;

  return { target, arch };
}

/**
 * The build to offer, or null.
 *
 * Null when the platform is unknown, when nothing is published for it, or when the deployment holds
 * two builds for it and nothing said which processor this is. ⛔ That last case is deliberately a
 * refusal rather than a preference: picking Apple Silicon because it is the more common Mac would
 * hand every Intel Mac a binary that will not run, and the person has no way to tell before they
 * open it. A caller with no single answer sends people to the page that lists them all.
 */
export function chooseDownload(
  downloads: readonly IDesktopDownload[],
  platform: IBrowserPlatform,
): IDesktopDownload | null {
  if (!platform.target) return null;
  const forPlatform = downloads.filter((download) => download.target === platform.target);
  if (forPlatform.length === 0) return null;
  if (forPlatform.length === 1) return forPlatform[0];

  const exact = forPlatform.filter((download) => download.arch === platform.arch);
  return exact.length === 1 ? exact[0] : null;
}

const PLATFORM_NAMES: Record<string, string> = {
  darwin: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

const ARCHITECTURE_NAMES: Record<string, string> = {
  aarch64: 'Apple Silicon',
  x86_64: 'Intel',
};

/**
 * What the control says it will do, in the words a person uses.
 *
 * The architecture is named rather than implied. Somebody on an Intel Mac must be able to see that
 * the button offers an Apple Silicon build *before* they download it — which is the whole reason
 * the label carries a detail the icon cannot show on its own.
 */
export function downloadLabel(download: IDesktopDownload, t: TFn): string {
  return t('navigation.desktopDownload.label', {
    number: download.version,
    platform: platformLabel(download),
    size: formatSize(download.sizeBytes, t),
  });
}

/** A build's platform in words — `macOS, Apple Silicon` — with the Mac's processor named. */
export function platformLabel(build: { target: string; arch: string }): string {
  const platform = PLATFORM_NAMES[build.target] ?? build.target;
  const architecture =
    build.target === 'darwin' ? (ARCHITECTURE_NAMES[build.arch] ?? build.arch) : null;
  return architecture ? `${platform}, ${architecture}` : platform;
}

/**
 * Binary-prefixed, whole numbers.
 *
 * Jay, 2026-09-22, of `17.0 MB`: *"show download size without decimals"*. The decimal was there to
 * tell 41.2 from 41.9, and nobody decides anything with that: the number answers *"is this a quick
 * download"*, and a tenth of a megabyte does not change the answer. `17.0` is worse than `17` twice
 * over — it is longer, and the trailing zero reads as a precision the figure does not have.
 *
 * ⚠️ Rounded, not truncated, so a 17.8 MB file does not advertise itself as 17 MB.
 */
export function formatSize(bytes: number, t: TFn): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return t('navigation.desktopDownload.unknownSize');
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${Math.round(value)} ${units[unit]}`;
}

/** Where the bytes come from, for the platform that was chosen. */
export function installerUrl(apiBaseUrl: string, download: IDesktopDownload): string {
  return `${apiBaseUrl.replace(/\/+$/, '')}/desktop/download/${download.target}/${download.arch}/latest/installer`;
}
